/*
  Sign in / create account page (snippets/customer-auth.liquid): the SIGN IN | CREATE ACCOUNT tabs, activate mode,
  show password and the register_return_to cookie. assets/login.js still owns sign-in, password recovery and
  the loyalty token exchange; assets/customer-auth.js owns create-account validation.
*/
(() => {
  const root = document.querySelector("[data-hb-auth]");
  if (!root || root.dataset.hbAuthReady) return;
  root.dataset.hbAuthReady = "1";

  const html = document.documentElement;
  const tablist = root.querySelector('[role="tablist"]');
  const tabs = Array.from(root.querySelectorAll("[data-auth-tab]"));
  const panels = {
    login: root.querySelector('[data-auth-panel="login"]'),
    register: root.querySelector('[data-auth-panel="register"]'),
  };
  const routes = { login: root.dataset.loginUrl, register: root.dataset.registerUrl };
  const registerForm = panels.register?.querySelector("form");
  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
  let view = root.dataset.view === "register" ? "register" : "login";

  const isActivateHash = () => (location.hash || "").toLowerCase().includes("activate");
  const isActivateMode = () => html.classList.contains("register-activate-mode");

  // #recover and #login belong to the Sign in tab and #activate to Create account, so each is dropped when
  // moving to the other tab.
  function hashFor(next) {
    const hash = location.hash || "";
    if (next === "register" && /^#(recover|login)$/i.test(hash)) return "";
    if (next === "login" && hash.toLowerCase().includes("activate")) return "";
    return hash;
  }

  function urlFor(next) {
    return routes[next] + location.search + hashFor(next);
  }

  function visibleHeading(panel) {
    return Array.from(panel.querySelectorAll("[data-auth-heading]")).find((heading) => heading.getClientRects().length);
  }

  // Leaving Sign in while "Reset your password" is open: come back to the sign-in form next time.
  function closeRecover() {
    const recover = panels.login?.querySelector(".recover-section");
    const login = panels.login?.querySelector(".login-section");
    if (!recover || !login || recover.style.display === "none") return;
    recover.style.display = "none";
    login.style.display = "block";
  }

  function bringIntoView(element) {
    const top = element.getBoundingClientRect().top;
    if (top >= 0 && top < window.innerHeight * 0.6) return;
    element.scrollIntoView({ block: "start", behavior: reducedMotion.matches ? "auto" : "smooth" });
  }

  function select(next, { focus = "heading", updateUrl = true } = {}) {
    if (!panels[next]) return;
    const changed = next !== view;
    if (changed && next === "register") closeRecover();
    view = next;
    root.dataset.view = next;

    tabs.forEach((tab) => {
      const selected = tab.dataset.authTab === next;
      tab.setAttribute("aria-selected", String(selected));
      tab.tabIndex = selected ? 0 : -1;
    });
    Object.entries(panels).forEach(([key, panel]) => {
      if (panel) panel.hidden = key !== next;
    });

    if (changed) {
      const panel = panels[next];
      panel.classList.remove("is-entering");
      void panel.offsetWidth; // restart the entrance animation
      panel.classList.add("is-entering");
    }

    if (updateUrl) {
      const target = urlFor(next);
      if (target !== location.pathname + location.search + location.hash) {
        history.replaceState(history.state, "", target);
      }
    }

    if (focus === "heading") {
      const heading = visibleHeading(panels[next]);
      if (heading) {
        heading.focus({ preventScroll: true });
        bringIntoView(tablist && !isActivateMode() ? tablist : heading);
      }
    }
  }

  // A tab chosen by the visitor. SIGN IN while "Reset your password" is open goes back to the sign-in form.
  function openTab(next) {
    if (next === "login" && view === "login" && /^#recover$/i.test(location.hash)) {
      closeRecover();
      history.replaceState(history.state, "", routes.login + location.search);
    }
    select(next);
  }

  function isPlainClick(event) {
    return event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
  }

  tabs.forEach((tab) => {
    tab.href = urlFor(tab.dataset.authTab);
    tab.addEventListener("click", (event) => {
      if (!isPlainClick(event)) return; // new tab or window: let the link open the other route
      event.preventDefault();
      openTab(tab.dataset.authTab);
    });
  });

  tablist?.addEventListener("keydown", (event) => {
    const index = tabs.indexOf(document.activeElement);
    if (index === -1) return;
    const rtl = getComputedStyle(root).direction === "rtl";
    let next = null;
    if (event.key === "ArrowRight") next = index + (rtl ? -1 : 1);
    else if (event.key === "ArrowLeft") next = index + (rtl ? 1 : -1);
    else if (event.key === "Home") next = 0;
    else if (event.key === "End") next = tabs.length - 1;
    else if (event.key === " ") {
      event.preventDefault();
      openTab(tabs[index].dataset.authTab);
      return;
    } else return;
    event.preventDefault();
    const tab = tabs[(next + tabs.length) % tabs.length];
    select(tab.dataset.authTab, { focus: "none" });
    tab.focus();
  });

  // "New to Huda Beauty? Create account" and "Already have an account? Sign in" switch tabs in place.
  // In activate mode the create-account fields are gone, so "Sign in" opens the sign-in page instead.
  root.querySelectorAll("[data-auth-switch]").forEach((link) => {
    // Keep return_to and the other query parameters when the link is followed (activate mode, new tab).
    // login.js adds its checked cart return to the Create account link after this runs.
    link.href = urlFor(link.dataset.authSwitch);
    link.addEventListener("click", (event) => {
      if (!isPlainClick(event) || isActivateMode()) return;
      event.preventDefault();
      select(link.dataset.authSwitch);
    });
  });

  // Recover password and Cancel (login.js swaps the sections): move focus to the heading that is now shown.
  root.querySelectorAll(".forgot-password-link, .cancel-link").forEach((link) => {
    link.addEventListener("click", () => {
      requestAnimationFrame(() => visibleHeading(panels.login)?.focus({ preventScroll: true }));
    });
  });

  function applyActivateMode() {
    if (!isActivateHash() || !registerForm) return false;
    html.classList.add("register-activate-mode");
    root.querySelectorAll(".register-name-fields, .register-marketing-fields").forEach((element) => element.remove());
    const action = registerForm.getAttribute("action") || location.pathname || "";
    registerForm.setAttribute("action", action.split("#")[0] + "#activate");
    return true;
  }

  // The help link on the sign-in page points to the register route with #activate. When the page already sits
  // on that route (after switching tabs), following it only changes the hash.
  window.addEventListener("hashchange", () => {
    if (applyActivateMode()) select("register", { focus: "heading" });
    else if (location.hash.toLowerCase() === "#recover" && view !== "login") select("login", { focus: "none" });
  });

  const showPassword = root.querySelector("#RegisterForm-show-password");
  const password = root.querySelector("#RegisterForm-password");
  showPassword?.addEventListener("change", () => {
    if (password) password.type = showPassword.checked ? "text" : "password";
  });

  // Runs after assets/customer-auth.js has validated the form (it stops invalid submits in the capture phase).
  registerForm?.addEventListener("submit", (event) => {
    if (event.defaultPrevented) return;
    // Back to the page the visitor came from once the account exists (layout/theme.liquid reads the cookie).
    // login.js puts a checked cart return on the "Create account" link when the visitor arrived with return_url.
    let returnTo = new URLSearchParams(location.search).get("return_to");
    if (!returnTo) {
      const link = root.querySelector(".create-account-link");
      try {
        returnTo = link ? new URL(link.href, location.origin).searchParams.get("return_to") : "";
      } catch {
        returnTo = "";
      }
    }
    if (returnTo) {
      document.cookie = `register_return_to=${encodeURIComponent(returnTo)};path=/;max-age=600;SameSite=Lax`;
    }
    // A loyalty session left from an earlier account must not carry over to the new one. The register
    // template also clears it on load; this covers accounts created from the sign-in page.
    window.HbOkendoLoyalty?.clearLoyaltySession?.();
  });

  // Opening tab: activate mode, then a #recover link, then the template's own tab.
  if (applyActivateMode()) select("register", { focus: "none" });
  else if (location.hash.toLowerCase() === "#recover") select("login", { focus: "none" });
  else select(view, { focus: "none", updateUrl: false });
})();
